import { IErrorResponse } from '@common/api.models';
import { useProjectId } from '@config/config.hooks';
import { IDownloadTransmittalDrawingsRequest, IFileResponse } from '@file/projectFile/projectFile.models';
import { downloadFile, downloadTransmittalDrawingsAsync } from '@file/projectFile/projectFile.services';
import {
  Button,
  CircularProgressBar,
  ComponentType,
  Form,
  Grid,
  Icon,
  Label,
  Modal,
  ToggleButton,
  useForm,
} from '@gcl/react-core';
import { useMutation } from '@tanstack/react-query';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import { AxiosError } from 'axios';
import * as React from 'react';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { transmittalDetailExportFormKeys } from './transmittalDetailPage.constants';

const exportOptions = [
  { key: 'transmittal', text: 'Transmittal' },
  { key: 'package', text: 'Package' },
];

export const coverSheetOptions = [
  { key: 'withoutCoverSheet', text: 'W/O Cover Sheet' },
  { key: 'withCoverSheet', text: 'With Cover Sheet' },
];
export interface ITransmittalDetailExportModalBtnProps {
  masterScheduleId: number;
  transmittalType?: ETransmittalType;
  transmittalId?: number;
  disabled?: boolean;
}
export const TransmittalDetailExportModalBtn: React.FunctionComponent<ITransmittalDetailExportModalBtnProps> = ({
  masterScheduleId,
  transmittalType,
  transmittalId,
  disabled = false,
}) => {
  const [isShow, setIsShow] = React.useState(false);
  const [isPackage, setIsPackage] = React.useState(transmittalType === null); // No transmittalType assume isPackage = true
  const [isWithCoverSheet, setIsWithCoverSheet] = React.useState(false);
  const [isIncludeIceDesignCertifiedOnly, setIsIncludeIceDesignCertifiedOnly] = React.useState(true);

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const onRenderBody = () => {
    return (
      <Form
        useFormMethods={useFormMethods}
        render={(formFieldMethods) => {
          return (
            <Grid container rowSpacing={2}>
              <Label labelFor={transmittalDetailExportFormKeys.PACKAGE}>Package Drawings</Label>
              {transmittalType != null && (
                <Grid item flex="1 1 100%">
                  <ToggleButton
                    value={isPackage}
                    width={200}
                    height={30}
                    type={ComponentType.Primary}
                    checkedText={
                      <>
                        <Icon fontWeight={600} iconName="FileEarmarkSpreadsheet" className="gcl-mr-sm" />
                        Package
                      </>
                    }
                    uncheckedText={
                      <>
                        <Icon fontWeight={600} iconName="FileEarmarkSpreadsheetFill" className="gcl-mr-sm" />
                        Transmittal
                      </>
                    }
                    shouldAlignCenter
                    onChange={({ checked }) => setIsPackage(checked)}
                  />
                </Grid>
              )}
              <Grid item flex="1 1 100%">
                <ToggleButton
                  value={isWithCoverSheet}
                  width={200}
                  height={30}
                  type={ComponentType.Primary}
                  checkedText={
                    <>
                      <Icon fontWeight={600} iconName="FileEarmarkSpreadsheet" className="gcl-mr-sm" />
                      With Cover Sheet
                    </>
                  }
                  uncheckedText={
                    <>
                      <Icon fontWeight={600} iconName="FileEarmarkSpreadsheetFill" className="gcl-mr-sm" />
                      W/O Cover Sheet
                    </>
                  }
                  shouldAlignCenter
                  onChange={({ checked }) => setIsWithCoverSheet(checked)}
                />
              </Grid>
              {transmittalType === ETransmittalType.ICE && (
                <>
                  <Label labelFor={transmittalDetailExportFormKeys.PACKAGE}>Include checked items only?</Label>
                  <Grid item flex="1 1 100%">
                    <ToggleButton
                      value={isIncludeIceDesignCertifiedOnly}
                      width={200}
                      height={30}
                      type={ComponentType.Primary}
                      checkedText={
                        <>
                          <Icon fontWeight={600} iconName="FileEarmarkSpreadsheet" className="gcl-mr-sm" />
                          Yes
                        </>
                      }
                      uncheckedText={
                        <>
                          <Icon fontWeight={600} iconName="FileEarmarkSpreadsheetFill" className="gcl-mr-sm" />
                          No
                        </>
                      }
                      shouldAlignCenter
                      onChange={({ checked }) => setIsIncludeIceDesignCertifiedOnly(checked)}
                    />
                  </Grid>
                </>
              )}
              <Grid
                item
                flex="0 1 30px"
                className="gcl-pl-xs"
                sx={[isLoading && { visibility: 'visible' }, !isLoading && { display: 'none' }]}
              >
                <CircularProgressBar value={progress} />
              </Grid>
            </Grid>
          );
        }}
      />
    );
  };

  const projectId = useProjectId();

  const onClickExport = (isZip: boolean) => {
    mutate({
      projectId,
      masterScheduleId,
      transmittalId,
      isZip,
      isPackage,
      shouldNotChop: transmittalType === ETransmittalType.SITE,
      shouldChopICEApprovalOnly: transmittalType !== ETransmittalType.SITE,
      shouldIncludeCoverSheet: isWithCoverSheet,
      shouldIncludeIceDesignCertifiedOnly: isIncludeIceDesignCertifiedOnly,
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setProgress(percentCompleted);
      },
    });
  };

  const [progress, setProgress] = React.useState(0);
  const { mutate, isLoading, error, reset } = useMutation<
    IFileResponse,
    AxiosError<IErrorResponse>,
    IDownloadTransmittalDrawingsRequest,
    any
  >(downloadTransmittalDrawingsAsync, {
    onError: () => {
      setProgress(0);
    },
    onSuccess: (data, variables, context) => {
      setProgress(0);
      downloadFile(data.file, data.filename);
    },
  });

  return (
    <>
      <ApiErrorDisplay errors={[{ error, genericError: 'Failed to download file', reset }]} />
      <Button
        disabled={disabled}
        prefixIconName="ArrowBarDown"
        type={ComponentType.Secondary}
        onClick={() => setIsShow(true)}
      >
        Export
      </Button>

      <Modal
        title="Export"
        show={isShow}
        onRenderBody={onRenderBody}
        onRenderFooter={() => {
          return (
            <>
              <Icon iconName="FiletypePdf" size={20} onClick={() => onClickExport(false)} className="gcl-ic-btn-md" />
              <Icon iconName="FileZip" size={20} onClick={() => onClickExport(true)} className="gcl-ic-btn-md" />
            </>
          );
        }}
        onHide={() => setIsShow(false)}
      />
    </>
  );
};
